import { Badge, Button, Dialog, Flex, Text, Box, Strong } from '@radix-ui/themes'
import { CheckIcon } from 'lucide-react'

const WHATSAPP_URL = 'https://wa.me/56993099380?text=' + encodeURIComponent('Hola, quiero mi página con Veekls')

const BENEFITS = [
  { title: '100% personalizada', body: 'diseñada a medida de tu marca y objetivos, no una plantilla genérica.' },
  {
    title: 'SEO y GEO incluidos',
    body: 'preparada para que te encuentren en Google y en los nuevos buscadores con IA.',
  },
  {
    title: 'Conectada a Veekls',
    body: 'queda integrada a nuestra plataforma, con todos los beneficios operativos que eso trae.',
  },
  {
    title: 'Más exposición automática',
    body: 'tu stock también se refleja en nuestro propio marketplace, market.veekls.com, sin trabajo extra de tu parte.',
  },
  {
    title: 'Soporte y actualizaciones permanentes',
    body: 'incluidas en tu mensualidad Veekls, sin costos extra.',
  },
]

const EXAMPLES = [
  { label: 'garridoautos.cl', href: 'https://garridoautos.cl' },
  { label: 'ucars.cl', href: 'https://ucars.cl' },
  { label: 'vialbesa.cl', href: 'https://vialbesa.cl' },
  { label: 'automec.cl', href: 'https://automec.cl' },
  { label: 'demo.veekls.com', href: 'https://demo.veekls.com' },
]

interface WebsitePromoDialogProps {
  open: boolean
  /** Único gatillo de "descartar": agenda el próximo recordatorio (o lo cierra para siempre). */
  onDismiss: () => void
  /** Único gatillo de "conversión": clic en el CTA de WhatsApp. */
  onConvert: () => void
}

// Aviso deliberadamente sin salida implícita: nada de X, clic afuera, ni Escape.
// Las dos únicas salidas son los botones del pie — así no hay un tercer camino
// ambiguo que compita con "descartar" o "convertir".
export const WebsitePromoDialog = ({ open, onDismiss, onConvert }: WebsitePromoDialogProps) => (
  <Dialog.Root open={open}>
    <Dialog.Content
      maxWidth="560px"
      width="95%"
      onEscapeKeyDown={(event) => event.preventDefault()}
      onPointerDownOutside={(event) => event.preventDefault()}
      onInteractOutside={(event) => event.preventDefault()}
    >
      <Dialog.Title>
        <Text size="5" weight="bold">
          Tu página web puede estar 100% integrada con Veekls
        </Text>
      </Dialog.Title>

      <Dialog.Description mb="3">
        <Badge variant="outline" color="red">
          Para ti que aún no tienes tu web con Veekls
        </Badge>
      </Dialog.Description>

      <Box>
        <Text as="p" size="2" color="gray" className="leading-relaxed" mb="4">
          Hoy construimos sitios 100% a medida de cada cliente, con SEO y GEO desde el diseño, y al quedar conectada a
          Veekls tu web se mantiene viva: la vamos actualizando por ti, sin que tengas que preocuparte de nada más.
        </Text>

        <Flex direction="column" gap="2" mb="4">
          {BENEFITS.map((benefit) => (
            <Flex key={benefit.title} gap="2" align="start">
              <Flex
                align="center"
                justify="center"
                className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-red-50 text-red-600"
              >
                <CheckIcon size={12} strokeWidth={3} />
              </Flex>
              <Text size="2" color="gray">
                <Strong className="text-[#1c1917]">{benefit.title}:</Strong> {benefit.body}
              </Text>
            </Flex>
          ))}
        </Flex>

        <Text size="1" weight="bold" color="gray" className="uppercase tracking-wide" mb="2" as="p">
          Así se ven hoy, ya funcionando
        </Text>
        <Flex gap="2" wrap="wrap" mb="4">
          {EXAMPLES.map((example) => (
            <a
              key={example.href}
              href={example.href}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-800 no-underline transition-colors hover:bg-red-600 hover:text-white"
            >
              {example.label} ↗
            </a>
          ))}
        </Flex>

        <Flex justify="between" align="center" wrap="wrap" gap="2" mb="4" className="rounded-xl bg-red-50 p-4">
          <Box>
            <Text as="p" size="1" color="gray">
              Cargo único de habilitación
            </Text>
            <Text as="p" size="4" weight="bold" className="text-red-800">
              $350.000 + IVA
            </Text>
          </Box>
          <Text size="1" color="gray" className="max-w-[220px] text-right">
            Pago único de activación. El soporte y las actualizaciones quedan incluidos en tu mensualidad Veekls.
          </Text>
        </Flex>

        <Flex direction="column" gap="2">
          <Button size="3" color="red" asChild onClick={onConvert}>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Text weight="bold">Quiero mi página con Veekls</Text>
            </a>
          </Button>
          <Button size="2" variant="ghost" color="gray" onClick={onDismiss}>
            Ahora no, recordarme más tarde
          </Button>
        </Flex>
      </Box>
    </Dialog.Content>
  </Dialog.Root>
)
